import { mdiCow, mdiForestOutline, mdiSprout } from '@mdi/js';

/** @typedef {'sojabohnen' | 'rind' | 'holz'} Commodity */

/**
 * @typedef {Object} CommodityMetadata
 * @property {string} title
 * @property {string} icon
 * @property {'t' | 'Stk.' | 'm³'} units Units submitted to TRACES
 * @property {string} [displayUnits] Units shown in the form, if different from `units`
 * @property {number} [yieldPerHectare]
 * @property {Array<HSCode>} hsHeadings
 * @property {Partial<Record<HSCode, string>>} [hints] Help text shown next to the quantity field of each HS code
 */

export const HS_HEADING = {
  '0102': 'Rinder',
  '120190': 'Sojabohnen',
  '4403': 'Rohholz',
  '4401': 'Brennholz',
};

/** @typedef {keyof HS_HEADING} HSCode */

/**
 * German labels for the TRACES statement status.
 * @type {Record<import('~~/server/utils/soap-traces').TracesStatus, string>}
 */
export const STATUS_LABELS = {
  AVAILABLE: 'Verfügbar',
  SUBMITTED: 'Übermittelt',
  REJECTED: 'Abgelehnt',
  WITHDRAWN: 'Zurückgezogen',
  ARCHIVED: 'Archiviert',
  SUSPENDED: 'Ausgesetzt',
  UPDATED: 'Aktualisiert',
  GROUPED: 'Gruppiert',
  OBSOLETE: 'Veraltet',
  UNKNOWN: 'Unbekannt',
};

/**
 * Percentage estimate or deviation sent with netWeight (TRACES allows 0-25 for DOMESTIC).
 */
export const NET_WEIGHT_TOLERANCE_PERCENT = 25;

/** @type {Record<Commodity, CommodityMetadata>} */
export const COMMODITIES = {
  sojabohnen: {
    title: 'Sojabohnen',
    icon: mdiSprout,
    units: 't',
    yieldPerHectare: 4,
    hsHeadings: ['120190'],
    hints: {
      120190: `Bitte geben Sie hier Ihre durchschnittliche jährliche Sojaproduktion an. Die Menge wird mit einer Toleranz von ±${NET_WEIGHT_TOLERANCE_PERCENT} % gemeldet.`,
    },
  },
  rind: {
    title: 'Rinder',
    icon: mdiCow,
    units: 'Stk.',
    hsHeadings: ['0102'],
    hints: {
      '0102':
        'Bitte geben Sie hier die durchschnittliche jährliche Anzahl der in Verkehr gebrachten Rinder an',
    },
  },
  holz: {
    title: 'Holz',
    icon: mdiForestOutline,
    units: 'm³',
    displayUnits: 'fm',
    hsHeadings: ['4403', '4401'],
    hints: {
      4403: 'Bitte geben Sie hier Ihre durchschnittliche jährliche Rohholzproduktion an',
      4401: 'Bitte geben Sie hier Ihre durchschnittliche jährliche Brennholzproduktion an',
    },
  },
};

/**
 * Feldstücknutzungsart (`fnar_code`) supersets that schläge of each commodity belong to.
 * @type {Object<string, Array<string>>}
 */
export const FNAR = {
  sojabohnen: ['A'],
  rind: ['G', 'L', 'D'],
};

export const EMPTY_GEOJSON = Object.freeze({
  type: 'FeatureCollection',
  features: [],
});

export const COMMODITY_KEYS = /** @type {Array<Commodity>} */ (Object.keys(COMMODITIES));

/** @typedef {'OTP' | 'SMS' | 'IDA' | 'AMA' | 'USP'} LoginProvider */

/** @typedef {'name' | 'address' | 'identifierType' | 'identifierValue'} UserDataField */

/** @type {Record<LoginProvider, Array<UserDataField>>} */
export const LOGIN_PROVIDED_FIELDS = {
  OTP: [],
  SMS: ['identifierType', 'identifierValue'],
  IDA: ['name', 'address'],
  AMA: ['name', 'address', 'identifierType', 'identifierValue'],
  USP: ['name', 'address', 'identifierType', 'identifierValue'],
};

/**
 * Login providers whose LFBIS number can be used for submitting cattle (reported to AMA).
 * @type {Array<LoginProvider>}
 */
export const CATTLE_LOGIN_PROVIDERS = ['AMA', 'SMS'];

/**
 * Login providers whose LFBIS number can be used for showing the user's fields and farms.
 * @type {Array<LoginProvider>}
 */
export const FIELDS_LOGIN_PROVIDERS = ['AMA', 'USP'];

/** @type {Array<UserDataField>} */
export const editableUserDataFields = ['name', 'address', 'identifierType', 'identifierValue'];

const VALID_EMAIL_REGEX =
  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

/**
 * @param {string} email
 * @returns {true|string}
 */
export const validateEmail = (email) => {
  return VALID_EMAIL_REGEX.test(email) || 'Bitte eine gültige E-Mail-Adresse eingeben';
};

/** Phone number in international format, e.g. `+436601234567` */
const VALID_PHONE_REGEX = /^\+\d{7,15}$/;

/**
 * @param {string} phone
 * @returns {boolean}
 */
export const isPhoneNumber = (phone) => VALID_PHONE_REGEX.test(phone);
